import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../../services/candidate/authService';
import { getProfileScore } from '../../../services/candidate/profileService';

const Dashboard = () => {
  const [searchData, setSearchData] = useState({
      jobQuery: '',
      location: ''
  });
  const [isSearching, setIsSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Card states
  const [stats, setStats] = useState({
    profileCompletion: 0,
    savedJobs: 0,
    appliedJobs: 0,
    profileViews: 0
  });
  
  const [profileTotalSections, setProfileTotalSections] = useState(5);
  const [profileCompletedSections, setProfileCompletedSections] = useState(0);
  
  // Fetch profile completion data
  useEffect(() => {
    const fetchProfileCompletion = async () => {
      try {
        setLoading(true);
        const result = await getProfileScore();
        //console.log('Profile completion response:', result);

        if (result.success) {
          // Update stats with overall score
          setStats(prev => ({
            ...prev,
            profileCompletion: result.data.overallScore,
            savedJobs: result.savedJobCount            
          }));

          // Update profile completion sections
          setProfileTotalSections(result.data.totalSections);
          setProfileCompletedSections(result.data.completedCount);

        } else {
          setError(result.message || 'Failed to fetch profile completion data');
        }
      } catch (err) {
        console.error('Error fetching profile completion:', err);
        setError('Unable to load profile completion data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfileCompletion();
  }, [navigate]);

  // Initialize animations
  useEffect(() => {
      const elements = document.querySelectorAll('.fade-in-up');
      elements.forEach(el => {
          el.style.transform = 'translateY(0)';
          el.style.opacity = '1';
      });
  }, []);

  // Handle search
  const handleSearch = () => {
      const { jobQuery, location } = searchData;
      
      if (!jobQuery.trim()) {
          // Add subtle shake animation to job search
          const jobInput = document.getElementById('job-search');
          if (jobInput) {
              jobInput.classList.add('border', 'border-red-500');
              jobInput.focus();
              
              setTimeout(() => {
                  jobInput.classList.remove('border', 'border-red-500');
              }, 1000);
          }
          
          return;
      }
      
      // Show loading state
      setIsSearching(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('q', jobQuery.trim());
      if (location.trim()) {
          params.append('location', location.trim());
      }
      
      // Simulate API call delay and then redirect
      setTimeout(() => {
          // Navigate to job listings with search parameters
          navigate(`/candidate/jobs?${params.toString()}`);
          setIsSearching(false);
      }, 500);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
          handleSearch();
      }
  };

  // Handle tag button click
  const handleTagClick = (tagText) => {
      setSearchData(prev => ({
          ...prev,
          jobQuery: tagText
      }));
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0096C7] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md">
          <div className="bg-red-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-exclamation-triangle text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Oops! Something went wrong</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-gradient-to-r from-[#0096C7] to-[#4361EE] text-white px-6 py-2 rounded-lg hover:shadow-lg transition-shadow"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* SEO Settings */}
      <title>{`My Dashboard | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Welcome Header */}
      <div className="mb-8">
        <h1 className="text-xl md:text-2xl font-bold text-white">Welcome to Your Career Dashboard!</h1>
        <p className="text-gray-400 ">Manage your job search and profile all in one place</p>
      </div>

      {/* Job Search Bar - Prominent Placement */}
      <div className="mt-6 w-full mb-8">
        <div className="search-shadow bg-white rounded-2xl p-2 w-full border border-gray-200">
            <div className="flex flex-col md:flex-row w-full">
                <div className="flex-grow p-3 md:p-4">
                    <div className="flex items-center w-full">
                        <div className="w-full">
                            <div className="flex items-center mb-1">
                                <i className="fas fa-search text-gray-600 mr-2 text-sm"></i>
                                <label className="text-sm font-medium text-gray-600">
                                    Job title, keywords, or company
                                </label>
                            </div>
                            <input 
                                id="job-search"
                                type="text" 
                                className="w-full text-gray-800 text-lg p-2 bg-transparent focus:outline-none placeholder-gray-400"
                                placeholder="e.g. 'Frontend Developer', 'Project Manager', 'Data Scientist'"
                                value={searchData.jobQuery}
                                onChange={(e) => setSearchData(prev => ({...prev, jobQuery: e.target.value}))}
                                onKeyPress={handleKeyPress}
                            />
                        </div>
                    </div>
                </div>
                <div className="border-t md:border-t-0 md:border-l border-gray-200 p-3 md:p-4">
                    <div className="flex items-center h-full w-full">
                        <div className="w-full">
                            <div className="flex items-center mb-1">
                                <i className="fas fa-map-marker-alt text-gray-600 mr-2 text-sm"></i>
                                <label className="text-sm font-medium text-gray-600">
                                    Location
                                </label>
                            </div>
                            <input 
                                type="text" 
                                className="w-full text-gray-800 p-2 bg-transparent focus:outline-none placeholder-gray-400"
                                placeholder="City, country, or 'Remote'"
                                value={searchData.location}
                                onChange={(e) => setSearchData(prev => ({...prev, location: e.target.value}))}
                                onKeyPress={handleKeyPress}
                            />
                        </div>
                    </div>
                </div>
                <div className="p-3 md:p-4 flex items-center">
                    <button 
                        className="w-full md:w-auto bg-gradient-to-r from-[#0096C7] to-[#4361EE] hover:from-[#00a8e0] hover:to-[#4a6bf5] text-white font-semibold px-6 md:px-8 py-4 rounded-xl flex items-center justify-center transition-all duration-300 hover:shadow-lg hover:shadow-[#0096C7]/20"
                        onClick={handleSearch}
                        disabled={isSearching}
                    >                        
                      {isSearching ? (
                          <>
                              <i className="fas fa-spinner fa-spin mr-3"></i>
                              <span className="text-lg">Searching...</span>
                          </>
                      ) : (
                          <>
                              <i className="fas fa-search mr-3"></i>
                              <span className="text-lg">Search</span>
                          </>
                      )}
                    </button>
                </div>
            </div>
        </div>
        
        {/* Quick Search Tags */}        
        <div className="mt-6 text-center">
            <p className="text-gray-400 text-sm mb-3">Popular searches:</p>
            <div className="flex flex-wrap justify-center gap-2">
                {['Remote Jobs', 'Financial Analyst', 'Farm Operations Manager', 'Data Science', 'Product Manager', 'Medical Lab Technician'].map((tag, index) => (
                    <button 
                        key={index}
                        className="text-sm px-4 py-2 rounded-full bg-gray-200 text-gray-900 hover:bg-gray-600 hover:text-white transition"
                        onClick={() => handleTagClick(tag)}
                    >
                        {tag}
                    </button>
                ))}
            </div>
        </div>
      </div>

      {/* Stats Cards - Based on Existing Modules */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-16">
        {/* Card 1: Profile Completion - Now with real data */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Profile Completion</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.profileCompletion}%</h3>
              <p className="text-blue-500 text-xs mt-2">
                {stats.profileCompletion < 100 ? 'Complete for better matches' : 'Great job! Your profile is complete'}
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <i className="fas fa-user-check text-blue-600 text-xl"></i>
            </div>
          </div>
          <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-500" 
              style={{ width: `${stats.profileCompletion}%` }}
            ></div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-gray-500">
              {profileCompletedSections}/{profileTotalSections} required steps done
            </span>
            <Link 
              to="/candidate/profile" 
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              {stats.profileCompletion < 100 ? 'Complete Profile →' : 'View Profile →'}
            </Link>
          </div>
        </div>

        {/* Card 2: Saved Jobs */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Saved Jobs</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">{stats.savedJobs}</h3>
              <p className="text-purple-500 text-xs mt-2">
                Review and apply
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <i className="fas fa-bookmark text-purple-600 text-xl"></i>
            </div>
          </div>
          <div className="mt-8 flex items-center justify-between">
            <span className="text-xs text-gray-500">Ready to apply</span>
            <Link 
              to="/candidate/saved-jobs" 
              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              View Saved →
            </Link>
          </div>
        </div>

        {/* Card 3: Resume Builder - Coming Soon */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">Resume Builder</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">Coming Soon</h3>
              <p className="text-green-500 text-xs mt-2">
                Create a professional resume in minutes
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <i className="fas fa-file-alt text-green-600 text-xl"></i>
            </div>
          </div>
          <div className="mt-8 flex items-center justify-between">
            <span className="bg-green-100 text-green-600 text-xs px-2 py-1 rounded-full">
              Coming Soon
            </span>
            <Link 
              to="/resume-builder"
              target='_blank' 
              className="text-sm text-green-600 hover:text-green-700 font-medium"
            >
              Learn More →
            </Link>            
          </div>
        </div>

        {/* Card 4: AI Match Profile - Coming Soon */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500 relative overflow-hidden">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-gray-500 text-sm">AI Match Profile</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">Smart Match</h3>
              <p className="text-orange-500 text-xs mt-2">
                Get AI-powered job matches
              </p>
            </div>
            <div className="bg-orange-100 p-3 rounded-lg opacity-50">
              <i className="fas fa-robot text-orange-600 text-xl"></i>
            </div>
          </div>
          <div className="mt-8 flex items-center justify-between">
            <span className="bg-orange-100 text-orange-600 text-xs px-2 py-1 rounded-full">
              Coming Soon
            </span>
            <Link 
              to="/ai-match-profile"
              target='_blank' 
              className="text-sm text-orange-400 hover:text-orange-700 font-medium"
            >
              Learn More →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;